<template>
  <b-modal
    id="generate-modal"
    ref="modal"
    title="Gerar Carta Pax"
    size="lg"
    @show="onShow"
    @ok.prevent="onGenerate"
    ok-title="Gerar"
    cancel-title="Cancelar"
    :ok-disabled="generating"
  >
    <div class="generate-form">
      <!-- Filter mode -->
      <div class="mb-3">
        <label class="font-weight-bold">Selecionar participantes por:</label>
        <div class="d-flex gap-3 mt-2">
          <b-form-radio v-model="filterMode" value="groups">Grupos / Tags</b-form-radio>
          <b-form-radio v-model="filterMode" value="participants">Participantes específicos</b-form-radio>
        </div>
      </div>

      <!-- Group/Tag filters -->
      <div v-if="filterMode === 'groups'">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Grupos</label>
            <multiselect
              v-model="selectedGroups"
              :options="groups"
              :multiple="true"
              track-by="id"
              label="name"
              placeholder="Todos os grupos"
              :close-on-select="false"
            />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Tags</label>
            <multiselect
              v-model="selectedTags"
              :options="tags"
              :multiple="true"
              track-by="id"
              label="name"
              placeholder="Todas as tags"
              :close-on-select="false"
            />
          </div>
        </div>
      </div>

      <!-- Participant search -->
      <div v-if="filterMode === 'participants'">
        <div class="mb-3">
          <label class="form-label">Buscar participantes</label>
          <div class="participant-search-box">
            <input
              type="text"
              class="form-control"
              v-model="participantSearch"
              placeholder="Buscar por nome ou email..."
              @input="debounceSearchParticipants"
            />
          </div>
          <div class="participant-results mt-2" v-if="searchResults.length > 0">
            <div
              v-for="p in searchResults"
              :key="p.id"
              class="participant-result-item"
              @click="toggleParticipant(p)"
            >
              <b-form-checkbox
                :checked="isSelected(p.id)"
                @change="toggleParticipant(p)"
                class="mr-2"
              />
              <div>
                <strong>{{ p.name }}</strong>
                <small class="text-muted d-block">{{ p.email }}</small>
              </div>
            </div>
          </div>
          <div v-if="selectedParticipants.length > 0" class="mt-2">
            <small class="text-muted">{{ selectedParticipants.length }} participante(s) selecionado(s)</small>
            <div class="selected-chips mt-1">
              <span
                v-for="p in selectedParticipants"
                :key="p.id"
                class="selected-chip"
              >
                {{ p.name }}
                <i class="bx bx-x" @click="removeParticipant(p.id)"></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Delivery mode -->
      <div class="mb-3">
        <label class="font-weight-bold">Modo de entrega:</label>
        <div class="d-flex gap-3 mt-2">
          <b-form-radio v-model="mode" value="download">
            <i class="bx bx-download mr-1"></i> Download ZIP
          </b-form-radio>
          <b-form-radio v-model="mode" value="email">
            <i class="bx bx-envelope mr-1"></i> Enviar por email
          </b-form-radio>
        </div>
      </div>

      <!-- Generating indicator -->
      <div v-if="generating" class="text-center py-3">
        <b-spinner variant="primary" small></b-spinner>
        <span class="ml-2">Processando...</span>
      </div>

      <!-- Incremental resend shortcuts -->
      <div v-if="hasPreviousSends" class="incremental-resend">
        <div class="incremental-title">
          <i class="bx bx-history"></i>
          Reenvio rápido
          <small>({{ totalPreviousSends }} envios anteriores)</small>
        </div>
        <div class="incremental-actions">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="doResendFailed"
            :disabled="generating || failedCount === 0"
          >
            <i class="bx bx-refresh"></i>
            Reenviar só falhas ({{ failedCount }})
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="doResendChanged"
            :disabled="generating"
          >
            <i class="bx bx-transfer"></i>
            Reenviar só alterações
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect'
import services from '../services'

let searchTimeout = null

export default {
  name: 'GenerateModal',
  components: {
    Multiselect
  },
  props: {
    templateId: {
      type: [Number, String],
      required: true
    }
  },
  data () {
    return {
      filterMode: 'groups',
      selectedGroups: [],
      selectedTags: [],
      selectedParticipants: [],
      participantSearch: '',
      searchResults: [],
      mode: 'download',
      generating: false,
      groups: [],
      tags: [],
      statusCounts: {}
    }
  },
  computed: {
    totalPreviousSends () {
      let total = 0
      for (const k in this.statusCounts) total += this.statusCounts[k]
      return total
    },
    hasPreviousSends () {
      return this.totalPreviousSends > 0
    },
    failedCount () {
      return (this.statusCounts.failed || 0) + (this.statusCounts.bounced || 0)
    }
  },
  methods: {
    async onShow () {
      try {
        const [groupRes, tagRes, sendsRes] = await Promise.allSettled([
          services.getGroups(),
          services.getTags(),
          services.listSends(this.templateId, { limit: 1 })
        ])
        this.groups = groupRes.status === 'fulfilled' ? (groupRes.value.data.data || groupRes.value.data) : []
        this.tags = tagRes.status === 'fulfilled' ? (tagRes.value.data.data || tagRes.value.data) : []
        this.statusCounts = sendsRes.status === 'fulfilled' && sendsRes.value.data.meta
          ? (sendsRes.value.data.meta.status_counts || {})
          : {}
      } catch (e) {
        console.error(e)
      }
    },
    async doResendFailed () {
      this.generating = true
      try {
        const res = await services.resendFailed(this.templateId)
        this.$bvToast.toast(`${res.data.total} reenvio(s) enfileirado(s).`, {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        this.$refs.modal.hide()
      } catch (e) {
        this.$bvToast.toast('Erro ao reenviar: ' + (e.response?.data?.message || e.message), {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.generating = false
      }
    },
    async doResendChanged () {
      this.generating = true
      try {
        const res = await services.resendChanged(this.templateId)
        const msg = `${res.data.total} reenvio(s) enfileirado(s). ${res.data.unchanged} inalterado(s).`
        this.$bvToast.toast(msg, {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
        this.$refs.modal.hide()
      } catch (e) {
        this.$bvToast.toast('Erro ao reenviar: ' + (e.response?.data?.message || e.message), {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.generating = false
      }
    },
    debounceSearchParticipants () {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        this.searchParticipants()
      }, 400)
    },
    async searchParticipants () {
      if (!this.participantSearch || this.participantSearch.length < 2) {
        this.searchResults = []
        return
      }
      try {
        const res = await services.searchParticipants({
          search: this.participantSearch,
          limit: 20
        })
        this.searchResults = res.data.data || res.data
      } catch (e) {
        console.error(e)
      }
    },
    isSelected (id) {
      return this.selectedParticipants.some(p => p.id === id)
    },
    toggleParticipant (participant) {
      const index = this.selectedParticipants.findIndex(p => p.id === participant.id)
      if (index >= 0) {
        this.selectedParticipants.splice(index, 1)
      } else {
        this.selectedParticipants.push(participant)
      }
    },
    removeParticipant (id) {
      this.selectedParticipants = this.selectedParticipants.filter(p => p.id !== id)
    },
    async onGenerate () {
      const payload = { mode: this.mode }

      if (this.filterMode === 'participants') {
        if (this.selectedParticipants.length === 0) {
          this.$bvToast.toast('Selecione pelo menos um participante.', {
            title: 'Atenção',
            variant: 'warning',
            solid: true
          })
          return
        }
        payload.participant_ids = this.selectedParticipants.map(p => p.id)
      } else {
        if (this.selectedGroups.length > 0) {
          payload.group_ids = this.selectedGroups.map(g => g.id)
        }
        if (this.selectedTags.length > 0) {
          payload.tag_ids = this.selectedTags.map(t => t.id)
        }
      }

      this.generating = true
      try {
        if (this.mode === 'download') {
          const res = await services.generate(this.templateId, payload)
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'carta-pax.zip')
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
          this.$bvToast.toast('Download iniciado!', {
            title: 'Sucesso',
            variant: 'success',
            solid: true
          })
        } else {
          await services.generate(this.templateId, payload)
          this.$bvToast.toast('Emails sendo enviados em background.', {
            title: 'Sucesso',
            variant: 'success',
            solid: true
          })
        }
        this.$refs.modal.hide()
      } catch (e) {
        this.$bvToast.toast('Erro ao gerar cartas: ' + (e.response?.data?.message || e.message), {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.generating = false
      }
    }
  }
}
</script>

<style scoped>
.gap-3 {
  gap: 16px;
}

.incremental-resend {
  margin-top: 16px;
  padding: 12px 14px;
  background: #f8f9ff;
  border: 1px dashed #c7d2fe;
  border-radius: 6px;
}

.incremental-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #475569;
  margin-bottom: 10px;
}

.incremental-title small {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 4px;
}

.incremental-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.incremental-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.participant-result-item {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: background 0.15s;
}
.participant-result-item:hover {
  background: #f0f7ff;
}
.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e3f2fd;
  border-radius: 12px;
  font-size: 12px;
  color: #1565c0;
}
.selected-chip i {
  cursor: pointer;
  font-size: 14px;
}
.selected-chip i:hover {
  color: #c62828;
}
</style>
